import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "net nu";
  if (sec < 3600) return `${Math.floor(sec / 60)} min geleden`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} uur geleden`;
  return `${Math.floor(sec / 86400)} dag${Math.floor(sec / 86400) === 1 ? "" : "en"} geleden`;
}

export default function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // user not logged in or backend issue — ignore silently
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch { /* ignore */ }
  };

  const handleClick = async (n) => {
    if (!n.read_at) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.data?.order_id) nav(`/bestellingen/${n.data.order_id}`);
    load();
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 inline-flex items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100"
        aria-label="Meldingen"
        data-testid="notification-bell-button"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-4 text-center" data-testid="notification-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-[340px] max-w-[92vw] bg-white border border-zinc-200 rounded-lg shadow-xl overflow-hidden z-50" data-testid="notification-popover">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <div className="font-semibold text-sm text-[#1B2D5C]">Meldingen</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#5BC044] hover:underline inline-flex items-center gap-1" data-testid="notification-mark-all">
                <CheckCheck size={12} /> Alles gelezen
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-400">Geen meldingen</div>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 ${!n.read_at ? "bg-emerald-50/40" : ""}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read_at && <div className="w-1.5 h-1.5 rounded-full bg-[#5BC044] mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1B2D5C] truncate">{n.title}</div>
                    <div className="text-xs text-zinc-600 line-clamp-2 mt-0.5">{n.message}</div>
                    <div className="text-[10px] text-zinc-400 font-mono mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                  {n.read_at && <Check size={12} className="text-zinc-300 mt-1 shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
